import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Users, Loader2, CheckCircle, XCircle, Heart } from 'lucide-react';
import { useAuth } from '@/features/auth';
import { useTranslation } from '@parentpath/i18n';
import { useLocale } from '@parentpath/i18n';
import { getInviteByToken, acceptPartnerInvite, INVITE_EXPIRED } from '../services/partnerService';

type InviteStatus = 'loading' | 'valid' | 'invalid' | 'accepting' | 'accepted' | 'error';

interface InviteData {
  inviterName: string;
  inviterEmail: string;
  dueDate: string;
  childNumber: number;
  pregnancyId: string;
}

// Helper to store/retrieve pending invite token
const PENDING_INVITE_KEY = 'pendingInviteToken';

export function InvitePage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const { t } = useTranslation('partner');
  const { locale } = useLocale();

  const [status, setStatus] = useState<InviteStatus>('loading');
  const [inviteData, setInviteData] = useState<InviteData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isAccepting, setIsAccepting] = useState(false);
  // Einladung älter als 7 Tage - eigene Meldung statt "ungültig"
  const [isExpired, setIsExpired] = useState(false);

  // Store token in localStorage for after email confirmation
  useEffect(() => {
    if (token && !isAuthenticated) {
      localStorage.setItem(PENDING_INVITE_KEY, token);
    }
  }, [token, isAuthenticated]);

  // Accept invite function - used both manually and automatically
  const doAcceptInvite = useCallback(async (inviteToken: string) => {
    setIsAccepting(true);
    setStatus('accepting');
    setError(null);

    const { error: acceptError } = await acceptPartnerInvite(inviteToken);

    if (acceptError) {
      console.error('Accept invite error:', acceptError);
      setIsAccepting(false);
      setStatus('error');

      if (acceptError.code === INVITE_EXPIRED) {
        setIsExpired(true);
        setError(t('invite.expiredDescription'));
        localStorage.removeItem(PENDING_INVITE_KEY);
        return false;
      }

      setError(acceptError.message || t('invite.acceptError'));
      return false;
    }

    // Clear stored token on success
    localStorage.removeItem(PENDING_INVITE_KEY);

    setIsAccepting(false);
    setStatus('accepted');
    return true;
  }, [t]);

  // Fetch invite details
  useEffect(() => {
    async function fetchInvite() {
      if (!token) {
        setStatus('invalid');
        return;
      }

      try {
        const { data, error: fetchError } = await getInviteByToken(token);

        if (fetchError || !data) {
          console.error('Fetch invite error:', fetchError);
          setIsExpired(fetchError?.code === INVITE_EXPIRED);
          setStatus('invalid');
          setError(
            fetchError?.code === INVITE_EXPIRED
              ? t('invite.expiredDescription')
              : t('invite.invalidDescription')
          );
          // Clear stored token if invalid
          localStorage.removeItem(PENDING_INVITE_KEY);
          return;
        }

        setInviteData({
          inviterName: data.inviter?.display_name || data.inviter?.email?.split('@')[0] || t('invite.someone'),
          inviterEmail: data.inviter?.email || '',
          dueDate: data.pregnancy?.expected_due_date || '',
          childNumber: data.pregnancy?.child_number || 1,
          pregnancyId: data.pregnancy_id,
        });
        setStatus('valid');
      } catch (err) {
        console.error('Fetch invite exception:', err);
        setStatus('invalid');
        setError(t('invite.loadError'));
      }
    }

    fetchInvite();
  }, [token, t]);

  // Auto-accept invite when user is authenticated and invite is valid
  useEffect(() => {
    async function autoAccept() {
      if (!isAuthenticated || !user || !token || status !== 'valid') {
        return;
      }

      // Automatically accept the invite
      const success = await doAcceptInvite(token);

      if (success) {
        // Redirect to onboarding after 2 seconds (partner needs to complete onboarding)
        setTimeout(() => {
          navigate('/onboarding');
        }, 2000);
      }
    }

    autoAccept();
  }, [isAuthenticated, user, token, status, doAcceptInvite, navigate]);

  // Handle manual accept (fallback if auto-accept didn't trigger)
  const handleAcceptInvite = async () => {
    if (!token || !user) return;

    const success = await doAcceptInvite(token);

    if (success) {
      // Redirect to onboarding after 2 seconds
      setTimeout(() => {
        navigate('/onboarding');
      }, 2000);
    }
  };

  // Format due date
  const formatDueDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString(locale === 'en' ? 'en-US' : 'de-DE', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  // Loading state
  if (authLoading || status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-pp-orange animate-spin mx-auto mb-4" />
          <p className="text-white/70">{t('invite.loading')}</p>
        </div>
      </div>
    );
  }

  // Invalid invite
  if (status === 'invalid') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="max-w-md w-full glass-effect rounded-[15px] border border-white/25 p-8 text-center">
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-8 h-8 text-red-400" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">
            {isExpired ? t('invite.expiredTitle') : t('invite.invalidTitle')}
          </h1>
          <p className="text-white/70 mb-6">
            {error || t('invite.invalidDescription')}
          </p>
          <Link
            to="/"
            className="block w-full py-3 bg-pp-orange rounded-[10px] text-white font-semibold hover:bg-pp-orange/90 transition-colors text-center"
          >
            {t('invite.backToHome')}
          </Link>
        </div>
      </div>
    );
  }

  // Accepting state
  if (status === 'accepting') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-pp-orange animate-spin mx-auto mb-4" />
          <p className="text-white/70">{t('invite.accepting')}</p>
        </div>
      </div>
    );
  }

  // Accepted state
  if (status === 'accepted') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="max-w-md w-full glass-effect rounded-[15px] border border-white/25 p-8 text-center">
          <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-10 h-10 text-green-400" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">{t('invite.connectedTitle')}</h1>
          <p className="text-white/70 mb-4">
            {t('invite.connectedDescription', { name: inviteData?.inviterName })}
          </p>
          <p className="text-sm text-white/50">{t('invite.redirecting')}</p>
        </div>
      </div>
    );
  }

  // Error state (after accepting failed)
  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="max-w-md w-full glass-effect rounded-[15px] border border-white/25 p-8 text-center">
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-8 h-8 text-red-400" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">
            {isExpired ? t('invite.expiredTitle') : t('invite.errorTitle')}
          </h1>
          <p className="text-white/70 mb-6">
            {error || t('invite.errorDescription')}
          </p>
          <div className="space-y-3">
            {/* Ein erneuter Versuch hilft bei einer abgelaufenen Einladung nicht */}
            {!isExpired && (
              <button
                onClick={handleAcceptInvite}
                className="w-full py-3 bg-pp-orange rounded-[10px] text-white font-semibold hover:bg-pp-orange/90 transition-colors"
              >
                {t('invite.retry')}
              </button>
            )}
            <Link
              to="/"
              className="block w-full py-3 glass-effect border border-white/25 rounded-[10px] text-white font-medium hover:bg-white/10 transition-colors text-center"
            >
              {t('invite.backToHome')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Not authenticated - prompt to login/register
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="max-w-md w-full glass-effect rounded-[15px] border border-white/25 p-8">
          {/* Header */}
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-pink-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Heart className="w-8 h-8 text-pink-400" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">{t('invite.title')}</h1>
            <p className="text-white/70">
              {t('invite.description', { name: inviteData?.inviterName })}
            </p>
          </div>

          {/* Pregnancy Info */}
          <div className="bg-white/10 rounded-[10px] border border-white/20 p-4 mb-6">
            <div className="flex items-center gap-3 text-white">
              <Users className="w-5 h-5 text-pp-orange" />
              <div>
                <p className="font-medium">{t('invite.dueDate')}</p>
                <p className="text-sm text-white/50">{formatDueDate(inviteData?.dueDate || '')}</p>
              </div>
            </div>
          </div>

          {/* Auth Prompt */}
          <p className="text-sm text-white/70 text-center mb-4">
            {t('invite.authPrompt')}
          </p>

          <div className="space-y-3">
            <Link
              to={`/login?redirect=/invite/${token}`}
              className="block w-full py-3 bg-pp-orange rounded-[10px] text-white font-semibold hover:bg-pp-orange/90 transition-colors text-center"
            >
              {t('invite.login')}
            </Link>
            <Link
              to={`/register?redirect=/invite/${token}`}
              className="block w-full py-3 glass-effect border border-white/25 rounded-[10px] text-white font-medium hover:bg-white/10 transition-colors text-center"
            >
              {t('invite.register')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Valid invite and authenticated - show accept UI (fallback if auto-accept didn't trigger)
  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="max-w-md w-full glass-effect rounded-[15px] border border-white/25 p-8">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-pink-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Heart className="w-8 h-8 text-pink-400" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">{t('invite.title')}</h1>
          <p className="text-white/70">
            {t('invite.description', { name: inviteData?.inviterName })}
          </p>
        </div>

        {/* Pregnancy Info */}
        <div className="bg-white/10 rounded-[10px] border border-white/20 p-4 mb-6">
          <div className="flex items-center gap-3 text-white">
            <Users className="w-5 h-5 text-pp-orange" />
            <div>
              <p className="font-medium">{t('invite.dueDate')}</p>
              <p className="text-sm text-white/50">{formatDueDate(inviteData?.dueDate || '')}</p>
            </div>
          </div>
        </div>

        {/* Features */}
        <div className="mb-6">
          <p className="text-sm font-medium text-white/50 mb-3">{t('invite.featuresTitle')}</p>
          <ul className="space-y-2 text-sm text-white/70">
            <li className="flex items-center gap-2">
              <span className="w-5 h-5 bg-pp-orange/20 rounded-full flex items-center justify-center text-pp-orange text-xs">✓</span>
              {t('invite.feature1')}
            </li>
            <li className="flex items-center gap-2">
              <span className="w-5 h-5 bg-pp-orange/20 rounded-full flex items-center justify-center text-pp-orange text-xs">✓</span>
              {t('invite.feature2')}
            </li>
            <li className="flex items-center gap-2">
              <span className="w-5 h-5 bg-pp-orange/20 rounded-full flex items-center justify-center text-pp-orange text-xs">✓</span>
              {t('invite.feature3')}
            </li>
          </ul>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-500/20 border border-red-500/30 text-red-300 p-3 rounded-[10px] mb-4 text-sm">
            {error}
          </div>
        )}

        {/* Actions */}
        <div className="space-y-3">
          <button
            onClick={handleAcceptInvite}
            disabled={isAccepting}
            className="w-full py-3 bg-pp-orange rounded-[10px] text-white font-semibold hover:bg-pp-orange/90 transition-colors disabled:opacity-50 flex items-center justify-center"
          >
            {isAccepting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {t('invite.connecting')}
              </>
            ) : (
              t('invite.accept')
            )}
          </button>
          <Link
            to="/"
            className="block w-full py-3 glass-effect border border-white/25 rounded-[10px] text-white font-medium hover:bg-white/10 transition-colors text-center"
          >
            {t('invite.later')}
          </Link>
        </div>
      </div>
    </div>
  );
}
